import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, CheckCircle, Clock, Users, Zap,
  ChevronRight, Shield, TrendingUp, TrendingDown, Minus,
  Key, Laptop, Mail, MessageSquare, GitBranch, Database
} from 'lucide-react';
import { cases, agents, systemMetrics, requiresAttention, connectors } from '../data/caseData';
import { toast } from 'sonner';

const statusColors = {
  executing: 'bg-primary/20 text-primary',
  pending_approval: 'bg-slate-200 text-slate-700',
  verifying: 'bg-slate-100 text-slate-600',
  closed: 'bg-primary/10 text-primary',
  failed: 'bg-slate-900 text-white',
  open: 'bg-gray-100 text-gray-700'
};

const statusLabels = {
  executing: 'Executing',
  pending_approval: 'Approval',
  verifying: 'Verifying',
  closed: 'Completed',
  failed: 'Failed',
  open: 'Open'
};

const priorityColors = {
  p1: 'bg-slate-900',
  p2: 'bg-slate-400',
  p3: 'bg-primary',
  p4: 'bg-gray-400'
};

const TrustTrendIcon = ({ trend }) => {
  if (trend === 'up') return <TrendingUp className="w-3 h-3 text-primary" />;
  if (trend === 'down') return <TrendingDown className="w-3 h-3 text-slate-900" />;
  return <Minus className="w-3 h-3 text-gray-400" />;
};

const ConnectorIcon = ({ name }) => {
  const icons = {
    okta: Key,
    entra_id: Key,
    google_workspace: Mail,
    intune: Laptop,
    slack: MessageSquare,
    github: GitBranch,
    figma: Activity,
    salesforce: Database
  };
  const Icon = icons[name] || Activity;
  return <Icon className="w-4 h-4" />;
};

export const ControlTower = () => {
  const navigate = useNavigate();
  const [selectedDomain, setSelectedDomain] = useState('all');

  const activeCases = cases.filter(c => c.status !== 'closed');
  const filteredCases = selectedDomain === 'all'
    ? activeCases
    : activeCases.filter(c => {
      const agentDomain = c.cluster?.agents?.[0]?.replace('_agent', '');
      return agentDomain === selectedDomain;
    });

  return (
    <div className="min-h-screen bg-gray-50" data-testid="control-tower-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-black">Control Tower</h1>
          <p className="text-sm text-gray-500 mt-1">System health and active cases</p>
        </div>

        {/* Top Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg border text-card-foreground p-4 shadow-sm" data-testid="system-status-card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Autonomy Rate</span>
              <Activity className="w-4 h-4 text-primary" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{systemMetrics.autonomyRate}%</span>
              <span className="text-xs text-primary font-medium">Target: 90%+</span>
            </div>
            <div className="mt-3 h-1.5 w-full bg-secondary rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: `${systemMetrics.autonomyRate}%` }}
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border text-card-foreground p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Pending Approvals</span>
              <Clock className="w-4 h-4 text-slate-500" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{systemMetrics.pendingApprovals}</span>
              {systemMetrics.pendingApprovals > 0 && (
                <span className="text-xs text-slate-600 font-medium">Needs attention</span>
              )}
            </div>
          </div>

          <div className="bg-card rounded-lg border text-card-foreground p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Cases (24h)</span>
              <Zap className="w-4 h-4 text-primary" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">{systemMetrics.cases24h}</span>
              <span className="text-xs text-muted-foreground">MTTR: {systemMetrics.mttr}</span>
            </div>
          </div>

          <div className="bg-card rounded-lg border text-card-foreground p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Cost (24h)</span>
              <Activity className="w-4 h-4 text-primary" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold">${systemMetrics.cost24h}</span>
              <span className="text-xs text-muted-foreground">~$0.33/case</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Cases and Attention */}
          <div className="lg:col-span-2 space-y-6">
            {/* Requires Attention */}
            {requiresAttention.length > 0 && (
              <div className="bg-card rounded-lg border text-card-foreground shadow-sm overflow-hidden" data-testid="requires-attention-panel">
                <div className="px-4 py-3 border-b bg-muted/20">
                  <h2 className="text-sm font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-slate-600" />
                    Requires Attention ({requiresAttention.length})
                  </h2>
                </div>
                <div className="divide-y">
                  {requiresAttention.map((item) => (
                    <div
                      key={item.id}
                      className="px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => {
                        if (item.caseId) {
                          navigate(`/case/${item.caseId}`);
                        } else if (item.type === 'conflict') {
                          toast.info('Resource contention detected', {
                            description: item.description,
                            action: {
                              label: 'View Details',
                              onClick: () => navigate('/control-tower')
                            }
                          });
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${item.priority === 'high' ? 'bg-destructive' : 'bg-slate-400'
                          }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground font-medium uppercase tracking-wide">
                              {item.type}
                            </span>
                            <span className="text-sm font-medium truncate">{item.title}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            {item.requestedBy && <span>By: {item.requestedBy}</span>}
                            {item.blastRadius && <span>Blast: {item.blastRadius}</span>}
                            {item.deadlockRisk && <span>Deadlock risk: {item.deadlockRisk}</span>}
                            <span>{item.age} ago</span>
                          </div>
                        </div>
                        <button
                          className="text-xs px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.caseId) {
                              navigate(`/case/${item.caseId}`);
                            } else if (item.type === 'conflict') {
                              navigate('/case/case_conflict_001');
                            }
                          }}
                          data-testid={`review-btn-${item.id}`}
                        >
                          Review
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Cases */}
            <div className="bg-card rounded-lg border text-card-foreground shadow-sm overflow-hidden" data-testid="active-cases-list">
              <div className="px-4 py-3 border-b bg-muted/20 flex items-center justify-between">
                <h2 className="text-sm font-medium">Active Cases ({activeCases.length})</h2>
                <div className="flex items-center gap-2">
                  <select
                    value={selectedDomain}
                    onChange={(e) => setSelectedDomain(e.target.value)}
                    className="h-7 text-xs border rounded-md px-2 bg-background hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="all">All Domains</option>
                    <option value="identity">Identity</option>
                    <option value="endpoint">Endpoints</option>
                    <option value="saas">SaaS</option>
                  </select>
                  <Link
                    to="/cases"
                    className="text-xs text-primary hover:underline font-medium"
                  >
                    View All
                  </Link>
                </div>
              </div>
              <div className="divide-y">
                {filteredCases.map((caseItem) => (
                  <Link
                    key={caseItem.id}
                    to={`/case/${caseItem.id}`}
                    className="flex items-center gap-4 px-4 py-3 hover:bg-muted/50 transition-colors"
                    data-testid={`case-row-${caseItem.id}`}
                  >
                    <div className={`w-1 h-8 rounded-full ${caseItem.priority === 'p1' ? 'bg-slate-900' :
                      caseItem.priority === 'p2' ? 'bg-slate-400' :
                        caseItem.priority === 'p3' ? 'bg-primary' : 'bg-gray-400'
                      }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusColors[caseItem.status]}`}>
                          {statusLabels[caseItem.status]}
                        </span>
                        <span className="text-sm font-medium truncate">
                          {caseItem.typeLabel}: {caseItem.subject.name}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 truncate">{caseItem.intent.summary}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs text-muted-foreground font-medium">
                        {caseItem.sla?.remaining || 'Completed'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {caseItem.cluster?.agents?.length || 0} agents
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground/50" />
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Agents and Connectors */}
          <div className="space-y-6">
            {/* Agent Fleet */}
            <div className="bg-card rounded-lg border text-card-foreground shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b bg-muted/20">
                <h2 className="text-sm font-medium flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Agent Fleet
                </h2>
              </div>
              <div className="divide-y">
                {agents.map((agent) => (
                  <div
                    key={agent.id}
                    className="px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/agents/${agent.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${agent.status === 'active' ? 'bg-primary' : 'bg-slate-400'
                          }`} />
                        <span className="text-sm font-medium">{agent.domain}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{agent.trust}%</span>
                        <TrustTrendIcon trend={agent.trustTrend} />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      <span>{agent.actions24h} actions</span>
                      <span>{agent.currentCases} cases</span>
                      {agent.drift === 'warn' && (
                        <span className="text-slate-600 flex items-center gap-1 font-medium">
                          <AlertTriangle className="w-3 h-3" /> Drift
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2 border-t bg-muted/10">
                <Link
                  to="/agents"
                  className="text-xs text-primary hover:underline font-medium"
                >
                  View Performance Details
                </Link>
              </div>
            </div>

            {/* Connectors */}
            <div className="bg-card rounded-lg border text-card-foreground shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b bg-muted/20">
                <h2 className="text-sm font-medium flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" />
                  Connectors
                </h2>
              </div>
              <div className="p-4 grid grid-cols-2 gap-2">
                {connectors.slice(0, 8).map((conn) => (
                  <div
                    key={conn.id}
                    className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
                  >
                    <div className={`w-1.5 h-1.5 rounded-full ${conn.status === 'healthy' ? 'bg-primary' :
                      conn.status === 'degraded' ? 'bg-slate-400' : 'bg-slate-600'
                      }`} />
                    <ConnectorIcon name={conn.id} />
                    <span className="text-xs text-muted-foreground truncate">{conn.name}</span>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2 border-t bg-muted/10">
                <Link
                  to="/connectors"
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Manage Connectors
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControlTower;
